import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReceptions } from '../../hooks/useReceptions';
import { ReceptionFilters, ReceptionListItem } from '../../models/Reception';
import { ErrorState, LoadingState } from '../feedback';
import { ReceptionsHeader } from './ReceptionsHeader';
import { ReceptionsView } from './ReceptionsView';
import { ReceptionsModals, ReceptionsModalsRef } from './ReceptionsModals';

const createHeaderActions = (handleCreateReception: () => void, t: (key: string) => string, canCreate: boolean) => {
  if (!canCreate) return [];
  return [
    {
      id: 'create',
      label: t('button.create'),
      onClick: handleCreateReception,
      variant: 'primary' as const,
    },
  ];
};

/**
 * Componente principal del módulo de Recepciones
 */
const Main = () => {
  const { t } = useTranslation('receptionsModule');
  const { hasPermission } = usePermissions();
  const modalsRef = useRef<ReceptionsModalsRef>(null);

  // Permisos
  const canCreate = hasPermission('receptions.create');
  const canEdit = hasPermission('receptions.edit');
  const canDelete = hasPermission('receptions.delete');

  // Estado para controlar carga inicial
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [filters, setFilters] = useState<ReceptionFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sorting, setSorting] = useState<{
    field: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [{ label: t('title'), path: '/receptions' }], [t]);

  const processedFilters = useMemo(() => {
    const apiFilters: ReceptionFilters = { ...filters };

    if (searchTerm.trim()) {
      apiFilters.code = searchTerm.trim();
    }

    if (sorting) {
      apiFilters.sort_column = sorting.field as ReceptionFilters['sort_column'];
      apiFilters.sort_direction = sorting.direction;
    }

    return apiFilters;
  }, [filters, searchTerm, sorting]);

  // Hook de datos
  const { receptions, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, total } =
    useReceptions(processedFilters);

  // Marcar fin de carga inicial
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Handlers de modales
  const handleCreateReception = useCallback(() => {
    modalsRef.current?.openCreateModal();
  }, []);

  const handleEditReception = useCallback((reception: ReceptionListItem) => {
    modalsRef.current?.openEditModal(reception);
  }, []);

  const handleDeleteReception = useCallback((reception: ReceptionListItem) => {
    modalsRef.current?.openDeleteModal(reception);
  }, []);

  // Handlers de filtros
  const handleFiltersChange = useCallback((newFilters: ReceptionFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  // Determinar si hay filtros activos
  const hasActiveFilters = !!(filters.status || filters.date_from || filters.date_to || filters.carrier);

  // Acciones del header - Solo si tiene permiso de crear
  const headerActions = useMemo(
    () => createHeaderActions(handleCreateReception, t, canCreate),
    [handleCreateReception, t, canCreate],
  );

  // Configurar navegación de página
  usePageNavigation({
    breadcrumbs,
    headerActions,
  });

  // Mostrar spinner solo en carga inicial
  if (isInitialLoad) {
    return <LoadingState />;
  }

  // Mostrar error
  if (error) {
    return <ErrorState error={error as Error} onRetry={handleRefetch} />;
  }

  return (
    <div className="px-4 md:px-6 pt-4 pb-4">
      {/* Header con filtros */}
      <ReceptionsHeader
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        searchTerm={searchTerm}
        total={total}
      />

      {/* Vista principal */}
      <ReceptionsView
        receptions={receptions}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error as Error | null}
        total={total}
        onEdit={handleEditReception}
        onDelete={handleDeleteReception}
        onCreateReception={handleCreateReception}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={setSorting}
        hasActiveFilters={hasActiveFilters}
        hasSearchTerm={!!searchTerm}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {/* Modales */}
      <ReceptionsModals ref={modalsRef} />
    </div>
  );
};

export { Main };
